import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import {
  getAllNotariesWithWorkload,
  getUnassignedDossiers,
  assignDossierToNotary,
  batchAssignDossiers,
  updateNotaryWorkload,
} from '../../../services/notaryWorkspaceService';
import { useAuth } from '../../../contexts/AuthContext';

const formatCurrency = (n) =>
  n ? new Intl.NumberFormat('fr-CI', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 })?.format(n) : '—';

const formatDate = (d) =>
  d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const PRIORITY_CONFIG = {
  low: { label: 'Faible', color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200' },
  normal: { label: 'Normal', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  high: { label: 'Élevée', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  urgent: { label: 'Urgent', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
};

const AVAILABILITY_CONFIG = {
  available: { label: 'Disponible', color: 'text-green-700', bg: 'bg-green-50', dot: 'bg-green-500' },
  busy: { label: 'Occupé', color: 'text-amber-700', bg: 'bg-amber-50', dot: 'bg-amber-500' },
  unavailable: { label: 'Indisponible', color: 'text-red-600', bg: 'bg-red-50', dot: 'bg-red-500' },
};

const WorkloadBar = ({ active, max }) => {
  const pct = max > 0 ? Math.min(Math.round((active / max) * 100), 100) : 0;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-green-500';
  return (
    <div className="flex items-center space-x-2">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-system text-text-secondary w-12 text-right">{active}/{max}</span>
    </div>
  );
};

const AdminNotaryAssignment = () => {
  const { user } = useAuth();
  const [notaries, setNotaries] = useState([]);
  const [unassignedDossiers, setUnassignedDossiers] = useState([]);
  const [allDossiers, setAllDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assign'); // 'assign' | 'workload' | 'all'
  const [selectedDossiers, setSelectedDossiers] = useState(new Set());
  const [assignments, setAssignments] = useState({}); // dossierId -> { notaryId, priority }
  const [processing, setProcessing] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [workloadEdit, setWorkloadEdit] = useState({}); // notaryId -> { maxCapacity, availabilityStatus }
  const [savingWorkload, setSavingWorkload] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [notariesRes, unassignedRes, allRes] = await Promise.all([
      getAllNotariesWithWorkload(),
      getUnassignedDossiers(),
      supabase?.from('notaire_dossiers')
        ?.select('*, notary:notaries(id, name)')
        ?.order('created_at', { ascending: false })
        ?.limit(50),
    ]);
    setNotaries(notariesRes?.data || []);
    setUnassignedDossiers(unassignedRes?.data || []);
    setAllDossiers(allRes?.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAssignmentChange = (dossierId, field, value) => {
    setAssignments(prev => ({
      ...prev,
      [dossierId]: { ...(prev?.[dossierId] || { notaryId: '', priority: 'normal' }), [field]: value },
    }));
  };

  const toggleDossierSelection = (dossierId) => {
    setSelectedDossiers(prev => {
      const next = new Set(prev);
      if (next?.has(dossierId)) next?.delete(dossierId);
      else next?.add(dossierId);
      return next;
    });
  };

  const handleBatchAssign = async () => {
    const toAssign = Array.from(selectedDossiers)
      ?.map(id => ({ dossierId: id, notaryId: assignments?.[id]?.notaryId, priority: assignments?.[id]?.priority || 'normal' }))
      ?.filter(a => a?.notaryId);

    if (toAssign?.length === 0) {
      setErrorMsg('Veuillez sélectionner un notaire pour chaque dossier.');
      return;
    }

    setProcessing(true);
    setErrorMsg('');
    const { success, errors } = await batchAssignDossiers(toAssign, user?.id);
    setProcessing(false);

    if (errors > 0) {
      setErrorMsg(`${errors} assignation(s) ont échoué.`);
    }
    if (success > 0) {
      setSuccessMsg(`${success} dossier(s) assigné(s) avec succès !`);
      setTimeout(() => setSuccessMsg(''), 4000);
      setSelectedDossiers(new Set());
      setAssignments({});
      await loadData();
    }
  };

  const handleSingleAssign = async (dossierId) => {
    const assignment = assignments?.[dossierId];
    if (!assignment?.notaryId) {
      setErrorMsg('Veuillez sélectionner un notaire.');
      return;
    }
    setProcessing(true);
    setErrorMsg('');
    const { error } = await assignDossierToNotary(dossierId, assignment?.notaryId, user?.id, '', assignment?.priority || 'normal');
    setProcessing(false);
    if (error) {
      setErrorMsg(error);
    } else {
      setSuccessMsg('Dossier assigné avec succès !');
      setTimeout(() => setSuccessMsg(''), 3000);
      setAssignments(prev => { const n = { ...prev }; delete n?.[dossierId]; return n; });
      await loadData();
    }
  };

  const handleSaveWorkload = async (notaryId) => {
    const edit = workloadEdit?.[notaryId];
    if (!edit) return;
    setSavingWorkload(notaryId);
    await updateNotaryWorkload(notaryId, edit?.maxCapacity || 20, edit?.availabilityStatus || 'available');
    setSavingWorkload(null);
    setWorkloadEdit(prev => { const n = { ...prev }; delete n?.[notaryId]; return n; });
    await loadData();
  };

  const suggestNotary = (dossierId) => {
    // Suggest notary with lowest workload ratio
    const available = notaries?.filter(n => n?.workload?.[0]?.availability_status !== 'unavailable');
    if (available?.length === 0) return null;
    const sorted = [...available]?.sort((a, b) => {
      const aLoad = (a?.workload?.[0]?.active_dossiers_count || 0) / (a?.workload?.[0]?.max_capacity || 20);
      const bLoad = (b?.workload?.[0]?.active_dossiers_count || 0) / (b?.workload?.[0]?.max_capacity || 20);
      return aLoad - bLoad;
    });
    return sorted?.[0]?.id;
  };

  const handleAutoBalance = () => {
    const newAssignments = { ...assignments };
    unassignedDossiers?.forEach(d => {
      if (!newAssignments?.[d?.id]?.notaryId) {
        const suggested = suggestNotary(d?.id);
        if (suggested) {
          newAssignments[d?.id] = { notaryId: suggested, priority: 'normal' };
        }
      }
    });
    setAssignments(newAssignments);
    setSelectedDossiers(new Set(unassignedDossiers?.map(d => d?.id)));
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3]?.map(i => <div key={i} className="bg-white rounded-xl border border-border p-4 animate-pulse h-20" />)}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Icon name="UserCheck" size={18} className="text-blue-600" />
            </div>
            Attribution des dossiers
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">{unassignedDossiers?.length} dossier(s) non assigné(s) · {notaries?.length} notaire(s) disponible(s)</p>
        </div>
        <button onClick={loadData} className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors">
          <Icon name="RefreshCw" size={14} />
          <span>Actualiser</span>
        </button>
      </div>

      {/* Feedback messages */}
      {successMsg && (
        <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-xl">
          <Icon name="CheckCircle2" size={16} className="text-green-600" />
          <p className="text-sm text-green-700 font-medium">{successMsg}</p>
        </div>
      )}
      {errorMsg && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-xl">
          <Icon name="AlertCircle" size={16} className="text-red-500" />
          <p className="text-sm text-red-600">{errorMsg}</p>
          <button onClick={() => setErrorMsg('')} className="ml-auto"><Icon name="X" size={14} className="text-red-400" /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-0">
        {[
          { id: 'assign', label: 'Assigner', icon: 'UserPlus', badge: unassignedDossiers?.length },
          { id: 'workload', label: 'Charge de travail', icon: 'BarChart2' },
          { id: 'all', label: 'Tous les dossiers', icon: 'FolderOpen' },
        ]?.map(tab => (
          <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px ${
              activeTab === tab?.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <Icon name={tab?.icon} size={14} />
            {tab?.label}
            {tab?.badge > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">{tab?.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* ASSIGN TAB */}
      {activeTab === 'assign' && (
        <div className="space-y-4">
          {unassignedDossiers?.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center mx-auto mb-3">
                <Icon name="CheckCircle2" size={22} className="text-green-600" />
              </div>
              <p className="font-semibold text-gray-700">Tous les dossiers sont assignés</p>
              <p className="text-sm text-gray-500 mt-1">Aucun dossier en attente d'attribution.</p>
            </div>
          ) : (
            <>
              {/* Batch controls */}
              <div className="flex items-center justify-between flex-wrap gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelectedDossiers(new Set(unassignedDossiers?.map(d => d?.id)))}
                    className="text-xs font-medium text-blue-700 hover:underline">Tout sélectionner</button>
                  <span className="text-blue-300">·</span>
                  <button onClick={() => setSelectedDossiers(new Set())}
                    className="text-xs font-medium text-blue-700 hover:underline">Désélectionner</button>
                  {selectedDossiers?.size > 0 && (
                    <span className="text-xs text-blue-600 font-semibold">{selectedDossiers?.size} sélectionné(s)</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={handleAutoBalance}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-50 transition-colors">
                    <Icon name="Zap" size={13} />
                    Équilibrage auto
                  </button>
                  {selectedDossiers?.size > 0 && (
                    <button onClick={handleBatchAssign} disabled={processing}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors">
                      {processing ? <div className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="Send" size={13} />}
                      Assigner la sélection ({selectedDossiers?.size})
                    </button>
                  )}
                </div>
              </div>

              {/* Dossier list */}
              <div className="space-y-3">
                {unassignedDossiers?.map(dossier => {
                  const isSelected = selectedDossiers?.has(dossier?.id);
                  const assignment = assignments?.[dossier?.id] || { notaryId: '', priority: 'normal' };
                  const priorityConf = PRIORITY_CONFIG?.[assignment?.priority] || PRIORITY_CONFIG?.normal;
                  return (
                    <div key={dossier?.id} className={`bg-white rounded-xl border p-4 transition-all ${isSelected ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200'}`}>
                      <div className="flex items-start gap-3">
                        {/* Checkbox */}
                        <button onClick={() => toggleDossierSelection(dossier?.id)}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 hover:border-blue-400'}`}>
                          {isSelected && <Icon name="Check" size={11} color="white" strokeWidth={3} />}
                        </button>

                        {/* Dossier info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-lg">{dossier?.reference}</span>
                            <span className="text-xs text-gray-500">{formatDate(dossier?.created_at)}</span>
                          </div>
                          <p className="font-semibold text-gray-900 text-sm truncate">{dossier?.property_title || 'Bien non renseigné'}</p>
                          <p className="text-xs text-gray-500 truncate">{dossier?.property_address || '—'}</p>
                          <p className="text-xs text-gray-600 mt-1">{formatCurrency(dossier?.transaction_amount)}</p>
                        </div>

                        {/* Assignment controls */}
                        <div className="flex flex-col gap-2 flex-shrink-0 min-w-[200px]">
                          <select value={assignment?.notaryId} onChange={e => handleAssignmentChange(dossier?.id, 'notaryId', e?.target?.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                            <option value="">— Choisir un notaire —</option>
                            {notaries?.map(n => {
                              const avail = n?.workload?.[0]?.availability_status || 'available';
                              const active = n?.workload?.[0]?.active_dossiers_count || 0;
                              const max = n?.workload?.[0]?.max_capacity || 20;
                              const isFull = active >= max;
                              return (
                                <option key={n?.id} value={n?.id} disabled={avail === 'unavailable' || isFull}>
                                  {n?.name} ({active}/{max}) {avail === 'unavailable' ? '— Indisponible' : isFull ? '— Complet' : ''}
                                </option>
                              );
                            })}
                          </select>
                          <div className="flex gap-2">
                            <select value={assignment?.priority} onChange={e => handleAssignmentChange(dossier?.id, 'priority', e?.target?.value)}
                              className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 bg-white">
                              <option value="low">Faible</option>
                              <option value="normal">Normal</option>
                              <option value="high">Élevée</option>
                              <option value="urgent">Urgent</option>
                            </select>
                            <button onClick={() => handleSingleAssign(dossier?.id)} disabled={!assignment?.notaryId || processing}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-1">
                              <Icon name="UserCheck" size={12} />
                              Assigner
                            </button>
                          </div>
                          {assignment?.notaryId && (
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border self-start ${priorityConf?.bg} ${priorityConf?.color} ${priorityConf?.border}`}>
                              Priorité : {priorityConf?.label}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* WORKLOAD TAB */}
      {activeTab === 'workload' && (
        <div className="space-y-3">
          {notaries?.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <Icon name="Users" size={32} className="text-gray-400 mx-auto mb-3" />
              <p className="font-semibold text-gray-600">Aucun notaire vérifié</p>
            </div>
          ) : notaries?.map(notary => {
            const wl = notary?.workload?.[0];
            const active = wl?.active_dossiers_count || 0;
            const max = wl?.max_capacity || 20;
            const avail = wl?.availability_status || 'available';
            const availConf = AVAILABILITY_CONFIG?.[avail] || AVAILABILITY_CONFIG?.available;
            const isEditing = !!workloadEdit?.[notary?.id];
            const edit = workloadEdit?.[notary?.id] || { maxCapacity: max, availabilityStatus: avail };

            return (
              <div key={notary?.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{notary?.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{notary?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{notary?.office_address || notary?.city || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${availConf?.bg} ${availConf?.color}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${availConf?.dot}`} />
                      {availConf?.label}
                    </span>
                    <button onClick={() => setWorkloadEdit(prev => isEditing ? (({ [notary?.id]: _, ...rest }) => rest)(prev) : { ...prev, [notary?.id]: { maxCapacity: max, availabilityStatus: avail } })}
                      className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                      <Icon name={isEditing ? 'X' : 'Settings'} size={14} className="text-gray-500" />
                    </button>
                  </div>
                </div>

                <WorkloadBar active={active} max={max} />
                <p className="text-xs text-gray-500 mt-1">{active} dossier(s) actif(s) sur {max} max</p>

                {isEditing && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Capacité max</label>
                        <input type="number" value={edit?.maxCapacity} onChange={e => setWorkloadEdit(prev => ({ ...prev, [notary?.id]: { ...edit, maxCapacity: parseInt(e?.target?.value) || 20 } }))}
                          min={1} max={100}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Disponibilité</label>
                        <select value={edit?.availabilityStatus} onChange={e => setWorkloadEdit(prev => ({ ...prev, [notary?.id]: { ...edit, availabilityStatus: e?.target?.value } }))}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white">
                          <option value="available">Disponible</option>
                          <option value="busy">Occupé</option>
                          <option value="unavailable">Indisponible</option>
                        </select>
                      </div>
                    </div>
                    <button onClick={() => handleSaveWorkload(notary?.id)} disabled={savingWorkload === notary?.id}
                      className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
                      {savingWorkload === notary?.id ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sauvegarde...</> : <><Icon name="Save" size={14} />Sauvegarder</>}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ALL DOSSIERS TAB */}
      {activeTab === 'all' && (
        <div className="space-y-3">
          {allDossiers?.map(dossier => (
            <div key={dossier?.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-lg">{dossier?.reference}</span>
                    <span className="text-xs text-gray-500">{formatDate(dossier?.created_at)}</span>
                  </div>
                  <p className="font-semibold text-gray-900 text-sm truncate">{dossier?.property_title || 'Bien non renseigné'}</p>
                  <p className="text-xs text-gray-500 truncate">{dossier?.property_address || '—'}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  {dossier?.notary ? (
                    <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 px-2.5 py-1 rounded-full border border-green-200">
                      <Icon name="UserCheck" size={12} />
                      <span>{dossier?.notary?.name}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200 font-semibold">Non assigné</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminNotaryAssignment;
